import { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTRACT_STATUS_MAP, type ContractStatus } from "@goyone/shared";
import { api } from "../../lib/api";
import { date, money } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { DocPreview } from "../../components/DocPreview";
import { useToast } from "../../components/Toast";
import { Badge, ErrorState, Modal, PageHead } from "../../components/ui";
import { DeleteButton } from "../../components/DeleteDialog";
import { ClauseEditor, clauseLabel, type Clause } from "./ClauseEditor";

interface Milestone {
  title: string;
  amount: number;
  dueAt?: string | null;
  done?: boolean;
}

interface Contract {
  id: string;
  number: string;
  title: string;
  status: ContractStatus;
  startAt: string;
  endAt: string | null;
  value: string;
  terms: string | null;
  notes: string | null;
  milestones: Milestone[] | null;
  clauses: Clause[] | null;
  signedAt: string | null;
  signedByName: string | null;
  signatureUrl: string | null;
  account: { id: string; name: string } | null;
  quotation: { id: string; number: string; total: string } | null;
  invoices: Array<{
    id: string;
    number: string;
    status: string;
    total: string;
  }>;
}

/**
 * شاشة العقد — فتحٌ وتعديل.
 *
 * لم تكن للعقد شاشةٌ أصلًا: يُنشأ من نافذةٍ ثم لا يُفتح أبدًا، فبنودُه
 * وشروطُه ودفعاتُه تُكتب مرةً ولا تُراجع. وهنا تُقرأ وتُعدَّل، والحالة
 * تتحرّك من موضعها لا من صفٍّ في قائمة.
 */
export function ContractDetail() {
  const { id = "" } = useParams();
  /* `?from=account` — جاء من ملف العميل فيُمنح زرَّ عودةٍ إليه. */
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const doc = useQuery({
    queryKey: ["contract", id],
    queryFn: () => api<Contract>(`/sales/contracts/${id}`),
  });

  const setStatus = useMutation({
    mutationFn: (status: ContractStatus) =>
      api(`/sales/contracts/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: (_r, status) => {
      toast(status === "ACTIVE" ? "فُعّل العقد" : "أُنهي العقد");
      void queryClient.invalidateQueries({ queryKey: ["contract", id] });
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ"),
  });

  const cancel = useMutation({
    mutationFn: () =>
      api(`/sales/contracts/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      toast("أُلغي العقد — يبقى في السجلّ ملغًى");
      void queryClient.invalidateQueries({ queryKey: ["contract", id] });
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الإلغاء"),
  });

  const sign = useMutation({
    mutationFn: () => api(`/sales/contracts/${id}/sign`, { method: "POST" }),
    onSuccess: () => {
      toast("وُقّع العقد بختم الشركة");
      void queryClient.invalidateQueries({ queryKey: ["contract", id] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التوقيع"),
  });

  const saveClauses = useMutation({
    mutationFn: (next: Clause[]) =>
      api(`/sales/contracts/${id}`, {
        method: "PATCH",
        body: { clauses: next },
      }),
    onSuccess: () => {
      toast("حُفظت البنود");
      void queryClient.invalidateQueries({ queryKey: ["contract", id] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر حفظ البنود"),
  });

  if (doc.isError)
    return <ErrorState error={doc.error} onRetry={() => void doc.refetch()} />;

  const c = doc.data;
  if (!c) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 320 }} />
      </div>
    );
  }

  const status = CONTRACT_STATUS_MAP[c.status];
  const milestones = c.milestones ?? [];
  const paid = milestones.filter((m) => m.done);
  const clauses = c.clauses ?? [];
  /* موقَّعٌ وسارٍ = مجمَّد: وثيقةٌ للرؤية، لا يُحرَّر منها شيء بعدها. */
  const frozen = Boolean(c.signatureUrl) && c.status === "ACTIVE";
  /*
   * والملغيُّ مجمَّدٌ كذلك — والخادم يردّ تعديلَه.
   *
   * كان زرُّ «تعديل» يُعرض عليه فيُضغط فيُردّ برسالة: بابٌ مقفلٌ عليه
   * لافتةُ «ادخل». فيُعرض مكانَه وسمٌ يقول حالَه، ويبقى العقدُ يُقرأ
   * ويُعايَن ويُطبع كما هو — فالملغيُّ سجلٌّ يُراجَع لا شيءٌ يُخفى.
   */
  const cancelled = c.status === "CANCELLED";
  const canCancel = c.status === "DRAFT" || c.status === "ACTIVE";

  return (
    <div className="page">
      <PageHead
        title={`عقد ${c.number}`}
        subtitle={c.title}
        actions={
          <>
            {/* من جاء من ملف العميل يعود إليه — لا إلى قائمةٍ لم يمرّ بها. */}
            {params.get("from") === "account" && c.account?.id && (
              <Link to={`/crm/accounts/${c.account.id}`} className="btn">
                <Icon name="arrowLeft" size={15} />
                ملف العميل
              </Link>
            )}
            <Link to="/sales/contracts" className="btn">
              <Icon name="arrowLeft" size={15} />
              رجوع
            </Link>

            <button className="btn" onClick={() => setPreviewing(true)}>
              <Icon name="search" size={15} />
              معاينة وطباعة
            </button>

            {/*
              الموقَّع الساري وثيقةٌ للرؤية لا للتحرير — زرّ التعديل يختفي
              ومكانه وسمٌ يقولها، والخادم يردّ من حاول من بابٍ آخر.
            */}
            {cancelled ? (
              <Badge kind="danger">ملغًى — للرؤية فقط</Badge>
            ) : frozen ? (
              <Badge kind="success">موقَّع سارٍ — للرؤية فقط</Badge>
            ) : (
              <button className="btn" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
                تعديل
              </button>
            )}

            {/* الختم لا يُعاد: عقدٌ مختومٌ يُعرض ختمه لا يُختم ثانية. */}
            {!c.signatureUrl && c.status !== "CANCELLED" && (
              <button
                className="btn"
                onClick={() => sign.mutate()}
                disabled={sign.isPending}
              >
                <Icon name="edit" size={15} />
                {sign.isPending ? "جارٍ التوقيع" : "توقيع بختم الشركة"}
              </button>
            )}

            {c.status === "DRAFT" && (
              <button
                className="btn btn-primary"
                onClick={() => setStatus.mutate("ACTIVE")}
                disabled={setStatus.isPending}
              >
                تفعيل
              </button>
            )}
            {c.status === "ACTIVE" && (
              <button
                className="btn btn-primary"
                onClick={() => setStatus.mutate("COMPLETED")}
                disabled={setStatus.isPending}
                title="العقد جرى حتى تمّ — يُقفل منتهيًا"
              >
                إنهاء
              </button>
            )}

            {/* الإلغاءُ شهادةُ أنّه لم يجرِ — غيرُ الإنهاء وغيرُ الحذف. */}
            {canCancel && (
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (
                    window.confirm(
                      `إلغاء العقد ${c.number}؟ يُسجَّل كأنّه لم يجرِ، ويبقى في السجلّ للمراجعة.`,
                    )
                  )
                    cancel.mutate();
                }}
                disabled={cancel.isPending}
                title="العقد لم يجرِ — يُسجَّل كأن لم يكن ويبقى للمراجعة"
              >
                <Icon name="x" size={15} />
                {cancel.isPending ? "جارٍ الإلغاء" : "إلغاء"}
              </button>
            )}

            <DeleteButton
              entity="contract"
              id={c.id}
              endpoint={`/sales/contracts/${c.id}`}
              onDeleted={() => navigate("/sales/contracts")}
            />
          </>
        }
      />

      {setStatus.isError && (
        <div className="alert alert-danger mb-4">
          {setStatus.error instanceof Error
            ? setStatus.error.message
            : "تعذّرت العملية."}
        </div>
      )}

      <div className="doc-head">
        <div>
          <div className="doc-number">{c.number}</div>
          <div className="small mute">
            {c.account ? (
              <Link to={`/crm/accounts/${c.account.id}`}>{c.account.name}</Link>
            ) : (
              "—"
            )}
          </div>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">المدة</span>
          <span>
            {date(c.startAt)}
            {c.endAt && ` — ${date(c.endAt)}`}
          </span>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">القيمة</span>
          <span className="num strong">{money(c.value)}</span>
        </div>
        <div style={{ marginInlineStart: "auto" }}>
          <Badge kind={status?.kind}>{status?.label}</Badge>
        </div>
      </div>

      {c.quotation && (
        <div className="alert alert-info mb-4">
          <Icon name="receipt" size={18} />
          <div>
            صادرٌ عن عرض السعر{" "}
            <Link to={`/sales/quotations/${c.quotation.id}`}>
              {c.quotation.number}
            </Link>
          </div>
        </div>
      )}

      {c.invoices.length > 0 && (
        <div className="alert alert-info mb-4">
          <Icon name="wallet" size={18} />
          <div>
            فواتير هذا العقد:{" "}
            {c.invoices.map((i, idx) => (
              <span key={i.id}>
                {idx > 0 && "، "}
                <Link to={`/finance/invoices/${i.id}`}>{i.number}</Link>
              </span>
            ))}
          </div>
        </div>
      )}

      {milestones.length > 0 && (
        <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">الدفعات</h2>
            <span className="tiny mute">
              {paid.length} من {milestones.length} مُحصَّلة
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>الدفعة</th>
                  <th className="col-num">المبلغ</th>
                  <th>الاستحقاق</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((m, i) => (
                  <tr key={`${m.title}-${i}`}>
                    <td className="cell-strong">{m.title}</td>
                    <td className="num">{money(m.amount)}</td>
                    <td className="cell-mute">
                      {m.dueAt ? date(m.dueAt) : "—"}
                    </td>
                    <td>
                      <Badge kind={m.done ? "success" : "neutral"}>
                        {m.done ? "مُحصَّلة" : "مستحقّة"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* بنود العقد: صيغتُه الحقيقية، مرتَّبةً ومحرَّرة في موضعها. */}
      {/*
        القفل بالتوقيع الفعليّ لا بتاريخه.
        العقود التي فُعّلت قبل نظام التوقيع تحمل `signedAt` بلا صورةٍ — ختمٌ
        لم يقع — فقفلُها به يمنع صاحبها من كتابة بنودها بلا سببٍ حقيقي.
      */}
      {/*
        والملغيُّ مقفولُ البنود كذلك.
        كان القفلُ بالتوقيع وحده، فيبقى محرّرُ البنود مفتوحًا على عقدٍ ملغًى
        وأزرارُ حفظه ظاهرة — والخادمُ يردّ. فيُقفل بسببه ويُقال سببُه.
      */}
      <ContractClauses
        initial={clauses}
        locked={Boolean(c.signatureUrl) || cancelled}
        lockReason={
          cancelled ? "العقد ملغًى — البنود للرؤية" : "العقد موقَّع — البنود مقفلة"
        }
        saving={saveClauses.isPending}
        onSave={(next) => saveClauses.mutate(next)}
      />

      {/*
        بطاقة التوقيع لا تظهر إلا بتوقيعٍ فعليّ.
        عقدٌ يحمل تاريخًا بلا صورة كان يُظهر إطارًا مكتوبًا فيه «بلا صورة
        توقيع» — وهو يزعم توقيعًا لم يقع. وتاريخُ التفعيل يُذكر في مكانه
        أعلاه، فلا يضيع.
      */}
      {c.signedAt && c.signatureUrl && (
        <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">التوقيع</h2>
          </div>
          <div className="card-body">
            <div className="signature-box">
              <img src={c.signatureUrl} alt="توقيع الشركة" />
              <div className="col" style={{ gap: 2 }}>
                <span className="strong">{c.signedByName ?? "عن الشركة"}</span>
                <span className="tiny mute">وُقّع في {date(c.signedAt)}</span>
              </div>
            </div>

            {/*
              الحدّ يُقال في موضعه.
              «توقيع إلكتروني» تُفهم توثيقًا للهوية، وهذا ختمُ شركةٍ يوفّر
              خطوة الطبع واليد — ولا يُثبت أن العميل وقّع.
            */}
            <p className="tiny mute mt-4">
              هذا ختمُ الشركة على المستند، لا توقيعٌ موثَّقٌ للعميل: لا يُثبت
              هويّة الطرف الآخر ولا موافقته. لتوقيع العميل، أرسل العقد إليه
              ليوقّعه.
            </p>
          </div>
        </div>
      )}

      {(c.terms || c.notes) && (
        <div className="card">
          <div className="card-body">
            {c.terms && (
              <div className="mb-4">
                <div className="tiny mute mb-2">الشروط</div>
                <p style={{ whiteSpace: "pre-wrap" }}>{c.terms}</p>
              </div>
            )}
            {c.notes && (
              <div>
                <div className="tiny mute mb-2">ملاحظات</div>
                <p style={{ whiteSpace: "pre-wrap" }}>{c.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {editing && (
        <ContractEdit
          contract={c}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void queryClient.invalidateQueries({ queryKey: ["contract", id] });
            void queryClient.invalidateQueries({ queryKey: ["contracts"] });
            void queryClient.invalidateQueries({ queryKey: ["account"] });
            void queryClient.invalidateQueries({ queryKey: ["quotations"] });
            void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
          }}
        />
      )}

      {previewing && (
        <DocPreview
          path={`/print/contract/${c.id}`}
          title={`عقد ${c.number}`}
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  );
}

/**
 * بنود العقد: تحريرٌ محلّيّ ثم حفظٌ صريح.
 *
 * الحفظ عند كل حرفٍ يُرسل عشرات الطلبات ويجعل التراجع مستحيلًا، فالتحرير
 * محلّيّ والحفظ بضغطة. و«غير محفوظ» يُقال صراحةً كي لا يُغادر أحدٌ الشاشة
 * ظانًّا أن ما كتبه ثبت.
 *
 * والموقَّع لا يُحرَّر: تغيير بنود عقدٍ حُمل عليه ختمٌ يجعل التوقيع على نصٍّ
 * غير الذي وُقّع.
 */
function ContractClauses({
  initial,
  locked,
  lockReason,
  saving,
  onSave,
}: {
  initial: Clause[];
  locked: boolean;
  /** لِمَ قُفلت — توقيعًا أو إلغاءً. */
  lockReason: string;
  saving: boolean;
  onSave: (next: Clause[]) => void;
}) {
  const [clauses, setClauses] = useState<Clause[]>(initial);
  const [dirty, setDirty] = useState(false);

  /* يُعاد الضبط حين يصل من الخادم نصٌّ أحدث ولم يكن المستخدم يحرّر. */
  useEffect(() => {
    if (!dirty) setClauses(initial);
  }, [initial, dirty]);

  const change = (next: Clause[]) => {
    setClauses(next);
    setDirty(true);
  };

  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">بنود العقد</h2>
        {locked ? (
          <span className="tiny mute">{lockReason}</span>
        ) : (
          <span
            className="row"
            style={{ gap: "var(--sp-2)", marginInlineStart: "auto" }}
          >
            {dirty && (
              <span className="tiny" style={{ color: "var(--warning)" }}>
                غير محفوظ
              </span>
            )}
            <button
              className="btn btn-sm btn-primary"
              disabled={!dirty || saving}
              onClick={() => {
                onSave(clauses.filter((c) => c.body.trim()));
                setDirty(false);
              }}
            >
              {saving ? "جارٍ الحفظ" : "حفظ البنود"}
            </button>
          </span>
        )}
      </div>

      <div className="card-body">
        {locked && clauses.length > 0 ? (
          <div className="clauses">
            {clauses.map((c, i) => (
              <div key={c.id} className="clause">
                <div className="clause-head">
                  <span className="clause-no">{clauseLabel(i)}</span>
                  {c.title && <strong>{c.title}</strong>}
                </div>
                <p style={{ whiteSpace: "pre-wrap" }}>{c.body}</p>
              </div>
            ))}
          </div>
        ) : (
          <ClauseEditor clauses={clauses} onChange={change} disabled={locked} />
        )}
      </div>
    </div>
  );
}

/** تعديل بيانات العقد — ما يُكتب هنا يُرسل جزئيًّا فلا يُمسّ ما لم يتغيّر. */
function ContractEdit({
  contract,
  onClose,
  onSaved,
}: {
  contract: Contract;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(contract.title);
  const [startAt, setStartAt] = useState(contract.startAt.slice(0, 10));
  const [endAt, setEndAt] = useState(contract.endAt?.slice(0, 10) ?? "");
  const [value, setValue] = useState(String(Number(contract.value)));
  const [terms, setTerms] = useState(contract.terms ?? "");
  const [notes, setNotes] = useState(contract.notes ?? "");

  const save = useMutation({
    mutationFn: () =>
      api(`/sales/contracts/${contract.id}`, {
        method: "PATCH",
        body: {
          title,
          startAt: new Date(startAt).toISOString(),
          endAt: endAt ? new Date(endAt).toISOString() : null,
          value: Number(value) || 0,
          terms: terms || null,
          notes: notes || null,
        },
      }),
    onSuccess: onSaved,
  });

  const valid = title.trim().length >= 2 && startAt.length === 10;

  return (
    <Modal
      title={`تعديل عقد ${contract.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ" : "حفظ"}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : "تعذّر الحفظ."}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ce-title">
            عنوان العقد
          </label>
          <input
            id="ce-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ce-start">
            تاريخ البدء
          </label>
          <input
            id="ce-start"
            className="input"
            type="date"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ce-end">
            تاريخ الانتهاء
          </label>
          <input
            id="ce-end"
            className="input"
            type="date"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ce-value">
            قيمة العقد
          </label>
          <input
            id="ce-value"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ce-terms">
            الشروط
          </label>
          <textarea
            id="ce-terms"
            className="textarea"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ce-notes">
            ملاحظات
          </label>
          <textarea
            id="ce-notes"
            className="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
